import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Clock, Check, ArrowLeft, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => new Date().toISOString().split("T")[0];
const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};

const inputClass =
  "w-full bg-white/5 border border-white/10 text-white placeholder-white/30 px-4 py-3.5 rounded-none focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition-colors";

export default function Reschedule() {
  const { token } = useParams();
  const [booking, setBooking] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [isWeekend, setIsWeekend] = useState(false);
  const [picked, setPicked] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reschedule/${token}`)
      .then(({ data }) => {
        setBooking(data);
        if (data.slot_date) setDate(data.slot_date);
      })
      .catch(() => setNotFound(true));
  }, [token]);

  const loadSlots = useCallback(async (d) => {
    if (!d) return;
    setLoadingSlots(true);
    try {
      const { data } = await axios.get(`${API}/availability`, { params: { date: d } });
      setSlots(data.slots || []);
      setIsWeekend(data.is_weekend);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, []);

  useEffect(() => { loadSlots(date); }, [date, loadSlots]);

  const save = async () => {
    if (!picked) { toast.error("Please pick a new time."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/reschedule/${token}`, { slot_date: date, slot_time: picked });
      setBooking((b) => ({ ...b, slot_date: date, slot_time: picked }));
      setDone(true);
      toast.success("Your consultation has been rescheduled.");
    } catch (err) {
      toast.error(err?.response?.status === 409 ? "That time was just taken — pick another." : "Could not reschedule. Please try again.");
      if (err?.response?.status === 409) loadSlots(date);
    } finally {
      setSaving(false);
    }
  };

  const cancelBooking = async () => {
    if (!window.confirm("Cancel this consultation? Your time slot will be released.")) return;
    setSaving(true);
    try {
      await axios.post(`${API}/reschedule/${token}/cancel`);
      setCancelled(true);
      toast.success("Your consultation has been cancelled.");
    } catch {
      toast.error("Could not cancel. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] text-white font-sans flex items-center justify-center px-6 py-20" data-testid="reschedule-page">
      <div className="noise" />
      <div className="w-full max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 text-white/40 hover:text-gold overline mb-10 transition-colors">
          <ArrowLeft size={14} /> Marcus Montgomery Consulting
        </Link>

        {notFound ? (
          <div data-testid="reschedule-notfound" className="border border-white/10 bg-[#0F0F13] p-12 text-center">
            <h1 className="font-serif text-4xl mb-4">Booking not found</h1>
            <p className="text-white/60">This reschedule link is invalid or has expired. Please <Link to="/contact" className="text-gold underline">book again</Link>.</p>
          </div>
        ) : cancelled ? (
          <motion.div data-testid="cancel-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-white/10 bg-[#0F0F13] p-12 text-center">
            <h1 className="font-serif text-4xl mb-4">Consultation cancelled.</h1>
            <p className="text-white/60">Your time slot has been released. Changed your mind? You can <Link to="/contact" className="text-gold underline">book a new time</Link> whenever you're ready.</p>
          </motion.div>
        ) : done ? (
          <motion.div data-testid="reschedule-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="border border-gold/40 bg-gold/5 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gold text-black flex items-center justify-center mx-auto mb-6"><Check size={28} /></div>
            <h1 className="font-serif text-4xl mb-4">You're rescheduled.</h1>
            <p className="text-white/70">Your consultation is now set for <span className="text-gold">{booking.slot_date} at {fmtTime(booking.slot_time)} MT</span>. A fresh confirmation is on its way to your inbox.</p>
          </motion.div>
        ) : (
          <div className="border border-white/10 bg-[#0F0F13] p-8 md:p-12">
            <div className="overline text-gold mb-2">Reschedule</div>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-6">Pick a new time{booking ? `, ${booking.name.split(" ")[0]}` : ""}.</h1>
            {booking?.slot_date && (
              <div className="flex items-center gap-2 text-white/50 text-sm mb-8">
                <Calendar size={14} className="text-gold" /> Currently booked: {booking.slot_date} at {fmtTime(booking.slot_time)} MT
              </div>
            )}

            <label className="overline text-white/50 block mb-2">New date</label>
            <input data-testid="reschedule-date" type="date" min={todayStr()} className={`${inputClass} [color-scheme:dark] mb-6`} value={date} onChange={(e) => { setDate(e.target.value); setPicked(""); }} />

            <label className="overline text-white/50 mb-3 flex items-center gap-2"><Clock size={13} className="text-gold" /> New time <span className="text-white/30 normal-case tracking-normal">(Mountain Time)</span></label>
            {loadingSlots ? (
              <div className="text-white/40 text-sm py-4">Loading available times…</div>
            ) : isWeekend ? (
              <div className="text-white/50 text-sm py-4 border border-white/10 px-4 bg-white/5">Weekends are unavailable — please choose a weekday (Mon–Fri).</div>
            ) : slots.length === 0 ? (
              <div className="text-white/40 text-sm py-4">No times available for this date.</div>
            ) : (
              <div data-testid="reschedule-slots" className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-8">
                {slots.map((s) => (
                  <button key={s.time} data-testid={`reslot-${s.time}`} type="button" disabled={!s.available} onClick={() => setPicked(s.time)}
                    className={`px-2 py-3 text-sm border transition-colors ${picked === s.time ? "bg-gold text-black border-gold" : s.available ? "border-white/10 text-white/80 hover:border-gold hover:text-gold" : "border-white/5 text-white/20 line-through cursor-not-allowed"}`}>
                    {fmtTime(s.time)}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-2">
              <button data-testid="reschedule-submit" onClick={save} disabled={saving} className="w-full sm:w-auto bg-gold text-black overline px-10 py-4 hover:bg-[#F3E5AB] transition-colors disabled:opacity-60">
                {saving ? "Saving…" : "Confirm New Time"}
              </button>
              <button data-testid="cancel-booking" onClick={cancelBooking} disabled={saving} className="text-white/40 hover:text-red-400 overline transition-colors disabled:opacity-60">
                Cancel this consultation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
